import React, { useState } from 'react';
import {
    faBugSlash,
    faCaretLeft,
    faCaretRight,
    faCheckCircle,
    faClock,
    faDatabase,
    faFile,
    faGear,
    faLayerGroup,
    faList,
    faNoteSticky,
    faQuestionCircle,
    faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import 'react-reflex/styles.css';
import { ReflexContainer, ReflexElement, ReflexSplitter } from 'react-reflex';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button, Modal, Pagination } from '@quyva/shared-ui';
import {
    getDate,
    getFileIcon,
    getFileIconColor,
    getTime,
    handleHtmlStringToElement,
    isValidJsonString,
} from '@quyva/common';

const getAnswerObject = (answerObject) => {
    let answer = answerObject;

    if (isValidJsonString(answer)) {
        let level = 0;
        const validJsonLevels = 5;
        while (isValidJsonString(answer) && level < validJsonLevels) {
            answer = JSON.parse(answer);
            level++;
        }
    } else {
        answer = answerObject;
    }

    return answer;
};

const SqlQueryComponent = ({ query }) => {
    if (!query) {
        return (
            <div className='text-icons-info'>
                <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className='mr-2'
                />
                <span>Database query NOT generated</span>
            </div>
        );
    }

    return <>{query}</>;
};

const buildUniqueValues = (steps, field) => {
    if (!steps || steps.length === 0) {
        return [];
    }
    const values = steps
        .map((step) => step?.[field])
        .filter((value) => value);
    return [...new Set(values)];
};

const DebugSteps = ({ steps }) => {
    if (!steps || steps.length === 0) {
        return (
            <div className='text-icons-info'>
                <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className='mr-2'
                />
                <span>No debug steps recorded</span>
            </div>
        );
    }

    return (
        <div className='space-y-3'>
            {steps.map((step, index) => (
                <div
                    key={`${step.operation_name}-${index}`}
                    className='border rounded-lg p-3 bg-backgrounds-white'
                >
                    <div className='flex items-center justify-between'>
                        <div className='font-semibold'>
                            {index + 1}. {step.operation_name}
                        </div>
                        <div className='text-xs text-texts-secondary300'>
                            {step.step_type?.toUpperCase() || 'STEP'}
                        </div>
                    </div>
                    {/* <div className='text-sm mt-2 space-y-1'> */}
                    <div className='flex flex-col items-start text-sm mt-2 space-y-1'>
                        <div>
                            <span className='font-semibold'>Elapsed:</span>{' '}
                            {step.elapsed_ms != null
                                ? `${Number(step.elapsed_ms).toFixed(2)} ms`
                                : 'NA'}
                        </div>
                        {(step.input_tokens != null || step.output_tokens != null) && (
                            <div>
                                <span className='font-semibold'>Tokens:</span>{' '}
                                <span>
                                    input {step.input_tokens ?? 'NA'}, output {step.output_tokens ?? 'NA'}
                                    {step.completion_tokens != null || step.reasoning_tokens != null
                                        ? ` (${step.completion_tokens ?? 0} + ${step.reasoning_tokens ?? 0} reasoning)`
                                        : ''}
                                </span>
                            </div>
                        )}

                        {step.total_llm_cost != null && (
                            <div>
                                <span className='font-semibold'>LLM Cost:</span>{' '}
                                ${Number(step.total_llm_cost)}
                            </div>
                        )}
                        {step.retrieval_query && (
                            <div>
                                <span className='font-semibold'>Retrieval:</span>{' '}
                                {step.retrieval_query}
                            </div>
                        )}
                        {step.cypher_query && (
                            <div>
                                <span className='font-semibold'>Cypher:</span>{' '}
                                {step.cypher_query}
                            </div>
                        )}
                    </div>
                    {step.output_preview && (
                        <pre className='mt-2 text-xs whitespace-pre-wrap'>
                            {step.output_preview}
                        </pre>
                    )}
                </div>
            ))}
        </div>
    );
};

const SourceContent = ({ file, pageNumber, repoName }) => {
    if (!file)
        return (
            <div className='mr-8'>
                <FontAwesomeIcon
                    icon={faDatabase}
                    // color={getFileIconColor('.db')}
                    className='mr-2'
                />
                <span className='font-semibold mr-3'>Database Name :</span>

                <span>{repoName}</span>
            </div>
        );

    return (
        <div className='flex items-center'>
            <div className='w-1/2 truncate'>
                <FontAwesomeIcon icon={faFile} className='mr-2' />
                <span className='font-semibold mr-3'>File Name :</span>
                <FontAwesomeIcon
                    icon={getFileIcon(file)}
                    color={getFileIconColor(file)}
                    className='mr-2'
                />
                <span className='truncate text-base'>{file}</span>
            </div>

            <div className='pl-2'>
                <FontAwesomeIcon icon={faNoteSticky} className='mr-2' />
                <span className='font-semibold'>Page Number : </span>
                <span>{pageNumber ?? 'NA'}</span>
            </div>
        </div>
    );
};

const ChunkContent = ({ chunk }) => {
    if (!chunk) {
        return (
            <div className='h-[40vh] w-full flex items-center justify-center text-icons-info'>
                <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className='mr-2'
                />
                <span>No chunk data</span>
            </div>
        );
    }

    return (
        <div className='h-full w-full'>{handleHtmlStringToElement(chunk)}</div>
    );
};

const ContentContainer = ({ title, icon, content }) => {
    return (
        <div className='border rounded-xl'>
            <div className='w-full flex items-center bg-backgrounds-primary100 p-3 rounded-t-xl shadow-inner'>
                <FontAwesomeIcon icon={icon} className='mr-2' />
                <span className='font-semibold'>{title}</span>
            </div>
            <div className='p-3 bg-backgrounds-white rounded-b-xl'>
                {content}
            </div>
        </div>
    );
};

export const ChatDebugModal = ({
    isSmallScreen,
    setShowDebugModal,
    chatObject,
    repoName,
}) => {
    const [activeChunkData, setActiveChunkData] = useState(
        chatObject.chunksData && Array.isArray(chatObject.chunksData)
            ? chatObject.chunksData.find((chunk) => Number(chunk.rank) === 1) ??
            chatObject.chunksData[0]
            : null
    );
    const [activeIndex, setActiveIndex] = useState(0);

    const handleSourceChange = (index) => {
        const newActiveChunk =
            chatObject.chunksData && Array.isArray(chatObject.chunksData)
                ? chatObject.chunksData[index]
                : null;
        setActiveChunkData(newActiveChunk);
        setActiveIndex(index);
    };

    const originalAnswer = getAnswerObject(chatObject.answer);
    const debugInfo = chatObject.debugInfo;
    const debugSteps = debugInfo?.steps ?? [];
    const retrievalQueries = buildUniqueValues(debugSteps, 'retrieval_query');
    const cipherQueries = buildUniqueValues(debugSteps, 'cypher_query');

    if (debugInfo) {
        console.debug('[DEBUG_INFO] ChatDebugModal debug_info', {
            qid: chatObject.qid,
            debug_info: debugInfo,
        });
    }

    return (
        <Modal
            title={'Chat Debug View'}
            titleIcon={faBugSlash}
            iconColor={'icons-primary'}
            onClose={() => setShowDebugModal(false)}
        >
            <div className='w-[95vw] text-texts-secondary300'>
                <ReflexContainer orientation='vertical'>
                    {!isSmallScreen && (
                        <ReflexElement
                            className='left-pane mr-0.5 max-h-[79vh] overflow-auto'
                            minSize={300}
                            size={600}
                            maxSize={600}
                        >
                            <div className='pane-content w-full h-full bg-transparent p-3'>
                                <ContentContainer
                                    title={'Question'}
                                    icon={faQuestionCircle}
                                    content={chatObject.question}
                                />

                                <br />

                                <ContentContainer
                                    title={'Generated SQL Query'}
                                    icon={faDatabase}
                                    content={
                                        <SqlQueryComponent
                                            query={chatObject.sqlQuery}
                                        />
                                    }
                                />

                                <br />

                                <ContentContainer
                                    title={'Original Answer'}
                                    icon={faCheckCircle}
                                    content={
                                        <pre>
                                            {JSON.stringify(
                                                originalAnswer,
                                                null,
                                                2
                                            )}
                                        </pre>
                                    }
                                />

                                <br />

                                <ContentContainer
                                    title={'Retrieval Queries'}
                                    icon={faDatabase}
                                    content={
                                        retrievalQueries.length > 0 ? (
                                            <ul className='list-disc pl-5'>
                                                {retrievalQueries.map((query, index) => (
                                                    <li key={`${query}-${index}`}>{query}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className='text-icons-info'>
                                                <FontAwesomeIcon
                                                    icon={faTriangleExclamation}
                                                    className='mr-2'
                                                />
                                                <span>No retrieval queries captured</span>
                                            </div>
                                        )
                                    }
                                />

                                <br />

                                <ContentContainer
                                    title={'Cypher Queries'}
                                    icon={faDatabase}
                                    content={
                                        cipherQueries.length > 0 ? (
                                            <ul className='list-disc pl-5'>
                                                {cipherQueries.map((query, index) => (
                                                    <li key={`${query}-${index}`}>{query}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className='text-icons-info'>
                                                <FontAwesomeIcon
                                                    icon={faTriangleExclamation}
                                                    className='mr-2'
                                                />
                                                <span>No cipher queries captured</span>
                                            </div>
                                        )
                                    }
                                />

                                <br />

                                <ContentContainer
                                    title={'Debug Summary'}
                                    icon={faBugSlash}
                                    content={
                                        debugInfo ? (
                                            <div className='space-y-1 text-sm'>
                                                {debugInfo.total_processing_ms != null && (
                                                    <div>
                                                        <span className='font-semibold'>Total Processing Time:</span>{' '}
                                                        {debugInfo.total_processing_ms >= 1000
                                                            ? `${(debugInfo.total_processing_ms / 1000).toFixed(2)} s`
                                                            : `${Number(debugInfo.total_processing_ms).toFixed(0)} ms`}
                                                    </div>
                                                )}
                                                <div>
                                                    <span className='font-semibold'>Total Input Tokens:</span>{' '}
                                                    {debugInfo.total_input_tokens ?? 0}
                                                </div>
                                                {/* <div>
                                                    <span className='font-semibold'>Total Output Tokens:</span>{' '}
                                                    {debugInfo.total_output_tokens ?? 0}
                                                </div> */}
                                                <div>
                                                    <span className='font-semibold'>Total Output Tokens:</span>{' '}
                                                    {debugInfo.total_output_tokens ?? 0}
                                                    {(debugInfo.total_completion_tokens != null ||
                                                        debugInfo.total_reasoning_tokens != null) && (
                                                            <>
                                                                {' ('}
                                                                {debugInfo.total_completion_tokens ?? 0} completion +{' '}
                                                                {debugInfo.total_reasoning_tokens ?? 0} reasoning
                                                                {')'}
                                                            </>
                                                        )}
                                                </div>
                                                <div>
                                                    <span className='font-semibold'>Total LLM Cost:</span>{' '}
                                                    {debugInfo.total_llm_cost != null
                                                        ? `$${Number(debugInfo.total_llm_cost)}`
                                                        : '$0.00'}
                                                </div>
                                                <div>
                                                    <span className='font-semibold'>Total Embedding Cost:</span>{' '}
                                                    {debugInfo.total_embedding_cost != null
                                                        ? `$${Number(debugInfo.total_embedding_cost)}`
                                                        : '$0.00'}
                                                </div>
                                                {debugInfo?.kg_nodes_involved_count != null && (
                                                    <div>
                                                        <span className='font-semibold'>KG Nodes Involved:</span>{' '}
                                                        {debugInfo.kg_nodes_involved_count}
                                                    </div>
                                                )}
                                                <div>
                                                    <span className='font-semibold'>Steps:</span>{' '}
                                                    {debugSteps.length}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className='text-icons-info'>
                                                <FontAwesomeIcon
                                                    icon={faTriangleExclamation}
                                                    className='mr-2'
                                                />
                                                <span>No debug info available</span>
                                            </div>
                                        )
                                    }
                                />

                                <br />

                                <ContentContainer
                                    title={'Debug Steps'}
                                    icon={faList}
                                    content={<DebugSteps steps={debugSteps} />}
                                />
                            </div>
                        </ReflexElement>
                    )}

                    {!isSmallScreen && (
                        <ReflexSplitter
                            style={{
                                border: 0,
                                background: 'white',
                                width: '6px',
                                zIndex: 10,
                                height: '79vh',
                            }}
                        />
                    )}

                    <ReflexElement className='right-pane ml-0.5 max-h-[79vh] overflow-auto'>
                        <div className='pane-content w-full h-full bg-transparent p-3'>
                            <ContentContainer
                                title={'Source'}
                                icon={faLayerGroup}
                                content={
                                    <SourceContent
                                        file={
                                            activeChunkData &&
                                                activeChunkData.actualFileName
                                                ? activeChunkData.actualFileName
                                                : null
                                        }
                                        pageNumber={
                                            activeChunkData &&
                                                activeChunkData.pageNumber
                                                ? activeChunkData.pageNumber
                                                : null
                                        }
                                        repoName={repoName}
                                    />
                                }
                            />

                            <br />

                            <div className='w-full h-full border rounded-xl z-0'>
                                <ReflexContainer orientation='horizontal'>
                                    <ReflexElement className='header'>
                                        <div className='pane-content w-full h-full'>
                                            <div className='w-full h-full flex items-center justify-between bg-backgrounds-primary100 p-3 rounded-t-xl shadow-inner'>
                                                <div className='flex items-center'>
                                                    <FontAwesomeIcon
                                                        icon={faList}
                                                        className='mr-2'
                                                    />
                                                    <span className='font-semibold'>
                                                        Chunk
                                                    </span>
                                                </div>

                                                {chatObject.chunksData &&
                                                    Array.isArray(
                                                        chatObject.chunksData
                                                    ) &&
                                                    chatObject.chunksData
                                                        .length > 0 && (
                                                        <div className='font-semibold'>
                                                            <span className='mx-1'>
                                                                {activeIndex +
                                                                    1}
                                                            </span>
                                                            /
                                                            <span className='mx-1'>
                                                                {
                                                                    chatObject
                                                                        .chunksData
                                                                        .length
                                                                }
                                                            </span>
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    </ReflexElement>

                                    <ReflexElement
                                        className=' bg-backgrounds-white rounded-b-xl'
                                        style={{
                                            minHeight: '45vh',
                                            height: '45vh',
                                            maxHeight: '45vh',
                                        }}
                                    >
                                        <div className='pane-content h-full w-full p-3 overflow-auto'>
                                            <ChunkContent
                                                chunk={
                                                    activeChunkData &&
                                                        activeChunkData.pageContent
                                                        ? activeChunkData.pageContent
                                                        : null
                                                }
                                            />
                                        </div>
                                    </ReflexElement>
                                </ReflexContainer>
                            </div>

                            <br />

                            {chatObject.chunksData &&
                                Array.isArray(chatObject.chunksData) &&
                                chatObject.chunksData.length > 0 && (
                                    <div className='h-full w-full px-3 py-2 border rounded-xl bg-backgrounds-primary100'>
                                        <div className='flex justify-between items-center'>
                                            <div className='flex justify-center items-center text-texts-secondary300 truncate'>
                                                <FontAwesomeIcon
                                                    icon={faGear}
                                                    className='mr-2'
                                                />
                                                <div className='truncate font-semibold'>
                                                    Source Control
                                                </div>
                                            </div>
                                            <Pagination
                                                activeId={
                                                    chatObject.qid ?? null
                                                }
                                                itemsCount={
                                                    chatObject.chunksData &&
                                                        Array.isArray(
                                                            chatObject.chunksData
                                                        )
                                                        ? chatObject.chunksData
                                                            .length
                                                        : 0
                                                }
                                                itemsPerPage={1}
                                                handlePageChange={
                                                    handleSourceChange
                                                }
                                                previousLabel={
                                                    <Button
                                                        hoverText={
                                                            'Go to previous source'
                                                        }
                                                        icon={faCaretLeft}
                                                        type={'warning'}
                                                        disabled={
                                                            activeIndex === 0
                                                        }
                                                    />
                                                }
                                                nextLabel={
                                                    <Button
                                                        hoverText={
                                                            'Go to next source'
                                                        }
                                                        icon={faCaretRight}
                                                        type={'warning'}
                                                        disabled={
                                                            activeIndex ===
                                                            chatObject
                                                                .chunksData
                                                                .length -
                                                            1
                                                        }
                                                    />
                                                }
                                            />
                                        </div>
                                    </div>
                                )}
                        </div>
                    </ReflexElement>
                </ReflexContainer>
            </div>
        </Modal>
    );
};
